import { create } from 'zustand';
import { http } from '@/libs/http';
import { toast } from 'sonner';
import type { User } from '@/types';

interface UserState {
    users: User[];
    isLoading: boolean;
    error: string | null;
    
    // Core actions
    list: (search?: string, tenantId?: string, status?: string, roleId?: string) => Promise<User[]>;
    get: (id: string) => Promise<User>;
    create: (data: Partial<User>) => Promise<void>;
    update: (id: string, data: Partial<User>) => Promise<void>;
    delete: (id: string) => Promise<void>;
    select: (criteria: (users: User[]) => User) => User;
    
    // Additional actions
    jit: (email: string) => Promise<void>;
    listGroups: (userId: string) => Promise<any[]>;
    listRoles: (userId: string) => Promise<any[]>;
    addRole: (userId: string, roleId: string) => Promise<void>;
    removeRole: (userId: string, roleId: string) => Promise<void>;
    invite: (email: string, tenantId: string, roleIds: string[], groupIds: string[]) => Promise<any>;
}

export const useUsers = create<UserState>((set, get) => ({
    users: [],
    isLoading: false,
    error: null,

    list: async (search, tenantId, status, roleId) => {
        set({ isLoading: true, error: null });
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (tenantId) params.append('tenant_id', tenantId);
            if (status) params.append('status', status.toLowerCase());
            if (roleId) params.append('role_id', roleId);
            
            const queryStr = params.toString() ? `?${params.toString()}` : '';
            const payload = await http.get<any>(`/mgmt/v1/users${queryStr}`);
            const data = Array.isArray(payload) ? payload : payload.items || [];
            
            set({ users: data, isLoading: false });
            return data;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to load users';
            set({ error: errorMsg, isLoading: false });
            return [];
        }
    },

    get: async (id) => {
        const found = get().users.find((user) => user.id === id);
        if (found) return found;
        const data = await http.get<User>(`/mgmt/v1/users/${id}`);
        set({ users: [...get().users.filter((u) => u.id !== id), data] });
        return data;
    },

    create: async (data) => {
        const previousUsers = get().users;
        const tempId = `temp-${Date.now()}`;
        const tempUser = { ...data, id: tempId } as User;
        
        set({ users: [...previousUsers, tempUser] });

        try {
            const newUser = await http.post<User>('/mgmt/v1/users', data);
            set({ 
                users: get().users.map(u => u.id === tempId ? newUser : u) 
            });
            toast.success('User created successfully', { id: 'user-create-success' });
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to create user';
            set({ users: previousUsers, error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    update: async (id, data) => {
        const previousUsers = get().users;
        set({
            users: previousUsers.map(u => u.id === id ? { ...u, ...data } : u)
        });

        try {
            const updatedUser = await http.put<User>(`/mgmt/v1/users/${id}`, data);
            set({
                users: get().users.map(u => u.id === id ? updatedUser : u)
            });
            toast.success('User updated successfully');
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to update user';
            set({ users: previousUsers, error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    delete: async (id) => {
        const previousUsers = get().users;
        set({
            users: previousUsers.filter(u => u.id !== id)
        });

        try {
            await http.delete(`/mgmt/v1/users/${id}`);
            toast.success('User deleted successfully');
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to delete user';
            set({ users: previousUsers, error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    select: (criteria) => {
        return criteria(get().users);
    },

    jit: async (email) => {
        try {
            await http.post('/mgmt/v1/users/jit', { email });
            toast.success('JIT user provisioning initiated');
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to provision user via JIT';
            set({ error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    invite: async (email, tenantId, roleIds, groupIds) => {
        try {
            const response = await http.post('/mgmt/v1/users/invite', {
                email,
                tenant_id: tenantId,
                role_ids: roleIds,
                group_ids: groupIds
            });
            toast.success('Invitation sent successfully');
            return response;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to send invitation';
            set({ error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    listGroups: async (userId) => {
        return http.get(`/mgmt/v1/users/${userId}/groups`);
    },

    listRoles: async (userId) => {
        return http.get(`/mgmt/v1/users/${userId}/roles`);
    },

    addRole: async (userId, roleId) => {
        await http.post(`/mgmt/v1/users/${userId}/roles/${roleId}`, {});
    },

    removeRole: async (userId, roleId) => {
        await http.delete(`/mgmt/v1/users/${userId}/roles/${roleId}`);
    }
}));

export const useUser = (id?: string) => {
    const user = useUsers((state) =>
        id ? state.users.find((item) => item.id === id) ?? null : null,
    );

    const ensureId = (): string => {
        if (!id) throw new Error('User id is required.');
        return id;
    };

    return {
        user,
        get: () => useUsers.getState().get(ensureId()),
        update: (data: Partial<User>) => useUsers.getState().update(ensureId(), data),
        delete: () => useUsers.getState().delete(ensureId()),
        groups: {
            list: () => useUsers.getState().listGroups(ensureId()),
        },
        roles: {
            list: () => useUsers.getState().listRoles(ensureId()),
            create: (roleId: string) => useUsers.getState().addRole(ensureId(), roleId),
            delete: (roleId: string) => useUsers.getState().removeRole(ensureId(), roleId),
        },
    };
};
